import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import stockCarteSimService from '../../services/stockCarteSimService';
import {
  SimRechargeJob,
  SimRechargeJobStatus,
  StockCarteSim,
  StatutStock,
} from '../../types';

interface StockCarteSimState {
  list: StockCarteSim[];
  loading: boolean;
  error: string | null;
  activeRechargeJob: SimRechargeJob | null;
  rechargeJobCreating: boolean;
  rechargeJobPolling: boolean;
  rechargeJobError: string | null;
}

const initialState: StockCarteSimState = {
  list: [],
  loading: false,
  error: null,
  activeRechargeJob: null,
  rechargeJobCreating: false,
  rechargeJobPolling: false,
  rechargeJobError: null,
};

const getApiErrorMessage = (error: unknown): string => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { erreur?: unknown } } }).response;
    if (typeof response?.data?.erreur === 'string') {
      return response.data.erreur;
    }
  }

  return error instanceof Error ? error.message : 'Une erreur est survenue.';
};

export const isRechargeJobTerminal = (status?: SimRechargeJobStatus): boolean =>
  status === 'COMPLETED' || status === 'PARTIAL_FAILURE' || status === 'FAILED';

export const fetchSims = createAsyncThunk('stockSim/fetchAll', () =>
  stockCarteSimService.getAll()
);

export const createSim = createAsyncThunk(
  'stockSim/create',
  (data: { numero: string; modele?: string }) => stockCarteSimService.create(data)
);

export const updateSimStatut = createAsyncThunk(
  'stockSim/update',
  ({ id, statut, modele, numero }: { id: number; statut: StatutStock; modele?: string; numero?: string }) =>
    stockCarteSimService.update(id, { statut, modele, numero })
);

export const deleteSim = createAsyncThunk('stockSim/delete', async (id: number) => {
  await stockCarteSimService.delete(id);
  return id;
});

export const rechargerSims = createAsyncThunk(
  'stockSim/recharger',
  ({ ids, dateRecharge }: { ids: number[]; dateRecharge?: string }) =>
    stockCarteSimService.recharger(ids, dateRecharge)
);

export const createSimRechargeJob = createAsyncThunk<
  SimRechargeJob,
  { simIds: number[] },
  { rejectValue: string }
>(
  'stockSim/createRechargeJob',
  async ({ simIds }, { rejectWithValue }) => {
    try {
      return await stockCarteSimService.createRechargeJob(simIds);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  }
);

export const stopSimRechargeJob = createAsyncThunk<
  SimRechargeJob,
  number,
  { rejectValue: string }
>(
  'stockSim/stopRechargeJob',
  async (jobId, { rejectWithValue }) => {
    try {
      return await stockCarteSimService.stopRechargeJob(jobId);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  }
);

export const fetchSimRechargeJob = createAsyncThunk<
  SimRechargeJob,
  number,
  { rejectValue: string }
>(
  'stockSim/fetchRechargeJob',
  async (jobId, { rejectWithValue }) => {
    try {
      return await stockCarteSimService.getRechargeJob(jobId);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  }
);

const stockCarteSimSlice = createSlice({
  name: 'stockSim',
  initialState,
  reducers: {
    clearSimRechargeJob: (state) => {
      state.activeRechargeJob = null;
      state.rechargeJobCreating = false;
      state.rechargeJobPolling = false;
      state.rechargeJobError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSims.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchSims.fulfilled, (state, action) => { state.loading = false; state.list = action.payload; })
      .addCase(fetchSims.rejected, (state, action) => { state.loading = false; state.error = action.error.message ?? 'Erreur'; })
      .addCase(createSim.fulfilled, (state, action) => { state.list.unshift(action.payload); })
      .addCase(updateSimStatut.fulfilled, (state, action) => {
        const idx = state.list.findIndex((i) => i.id === action.payload.id);
        if (idx !== -1) state.list[idx] = action.payload;
      })
      .addCase(deleteSim.fulfilled, (state, action) => {
        state.list = state.list.filter((i) => i.id !== action.payload);
      })
      .addCase(rechargerSims.fulfilled, (state, action) => {
        action.payload.forEach((updated) => {
          const idx = state.list.findIndex((i) => i.id === updated.id);
          if (idx !== -1) state.list[idx] = updated;
        });
      })
      .addCase(createSimRechargeJob.pending, (state) => {
        state.rechargeJobCreating = true;
        state.rechargeJobError = null;
      })
      .addCase(createSimRechargeJob.fulfilled, (state, action) => {
        state.rechargeJobCreating = false;
        state.activeRechargeJob = action.payload;
      })
      .addCase(createSimRechargeJob.rejected, (state, action) => {
        state.rechargeJobCreating = false;
        state.rechargeJobError = action.payload ?? action.error.message ?? 'Impossible de créer la recharge.';
      })
      .addCase(stopSimRechargeJob.pending, (state) => {
        state.rechargeJobCreating = true;
        state.rechargeJobError = null;
      })
      .addCase(stopSimRechargeJob.fulfilled, (state, action) => {
        state.rechargeJobCreating = false;
        state.activeRechargeJob = action.payload;
      })
      .addCase(stopSimRechargeJob.rejected, (state, action) => {
        state.rechargeJobCreating = false;
        state.rechargeJobError = action.payload ?? action.error.message ?? 'Impossible d\'arrêter la recharge.';
      })
      .addCase(fetchSimRechargeJob.pending, (state) => {
        state.rechargeJobPolling = true;
        state.rechargeJobError = null;
      })
      .addCase(fetchSimRechargeJob.fulfilled, (state, action) => {
        state.rechargeJobPolling = false;
        state.activeRechargeJob = action.payload;
      })
      .addCase(fetchSimRechargeJob.rejected, (state, action) => {
        state.rechargeJobPolling = false;
        state.rechargeJobError = action.payload ?? action.error.message ?? 'Impossible de suivre la recharge.';
      });
  },
});

export const { clearSimRechargeJob } = stockCarteSimSlice.actions;
export default stockCarteSimSlice.reducer;
